import React from 'react';
import {useNavigate} from 'react-router-dom';
import {Alert, Button, Collapse, Table, Tabs} from 'antd';
import {
  attendingDinnerLabel,
  EventRegistrationStatus,
  formatCurrencyByLocalChapter,
  formatUtcToLocalDateTime,
  meetingPresentationLabel,
} from '../../../../../Util/commonUtility';
import useLocalization from '../../../../../Localization';
import './style.css';

export const ACTIVE_REP_STATUSES = [
  EventRegistrationStatus.PAID,
  EventRegistrationStatus.PAYMENT_CONFIRMED,
  EventRegistrationStatus.PAYMENT_PROCESSING,
  EventRegistrationStatus.FEE_WAIVER,
  EventRegistrationStatus.NO_CHARGE_EVENT,
];

const getSponsorshipOption = (sponsorship) =>
  sponsorship?.sponsor?.event_registration_options?.sponsorship;

/**
 * My Events: one entry per sponsorship. Its original purchase and every later "add reps" payment are
 * listed under it, so the sponsor sees everything they bought and which reps belong to it.
 */
const SponsorshipGroups = ({
  sponsorships,
  columns,
  eventId,
  eventLocalChapterData,
  renderTransactionActions,
}) => {
  const {ApplicationString} = useLocalization();
  const navigate = useNavigate();

  return (
    <>
      <Collapse className='sponsorship-groups' defaultActiveKey={[]}>
        {(sponsorships || []).map((sponsorship) => {
          const sponsor = sponsorship?.sponsor;
          const sponsorshipOption = getSponsorshipOption(sponsorship);
          const isSponsorshipActive = ACTIVE_REP_STATUSES.includes(
            sponsor?.status,
          );
          const hasUnpaidAddedVendorReps = Boolean(
            sponsorship?.has_unpaid_added_reps,
          );
          const limitMessage = !sponsorship?.can_add_reps
            ? sponsorship?.has_perk_limits
              ? ApplicationString['sponsorshipReps.limitReachedAll']
              : ApplicationString['sponsorshipReps.limitReached']
            : null;
          const perk = sponsorship?.event_sponsorship_perk;
          const tableColumns = (columns || [])
            .filter((column) => !column.hidden)
            .map((column) => {
              if (column.dataIndex === 'attending_presentation') {
                return {
                  ...column,
                  title: meetingPresentationLabel(
                    perk?.attendees_price,
                    ApplicationString,
                  ),
                };
              }
              if (column.dataIndex === 'attending_dinner') {
                return {
                  ...column,
                  title: attendingDinnerLabel(
                    perk?.dinner_with_speaker_price,
                    ApplicationString,
                  ),
                };
              }
              return column;
            });
          return (
            <Collapse.Panel
              key={String(sponsorship.sponsor_registration_id)}
              header={
                <div className='sponsorship-group-header'>
                  <div>
                    <div className='sponsorship-group-title'>
                      {sponsorshipOption?.name ||
                        ApplicationString['sponsorshipReps.sponsor']}
                      {sponsor?.attendee_company
                        ? ` - ${sponsor.attendee_company}`
                        : ''}
                    </div>
                    <div className='sponsorship-group-meta'>
                      {ApplicationString['sponsorshipReps.contact']}:{' '}
                      {sponsor?.attendee_first_name}{' '}
                      {sponsor?.attendee_last_name} ({sponsor?.attendee_email})
                      {sponsorship?.vendor_rep_limit > 0 && (
                        <span className='sponsorship-group-rep-count'>
                          {ApplicationString['sponsorshipReps.freeRepsUsed']}:{' '}
                          {sponsorship.free_reps_paid} /{' '}
                          {sponsorship.vendor_rep_limit}
                        </span>
                      )}
                    </div>
                  </div>
                  {sponsorship?.can_add_reps && (
                    <Button
                      type='primary'
                      className='common-button-refund sponsorship-add-reps-button'
                      onClick={(event) => {
                        event.stopPropagation();
                        navigate(
                          `/addVendorReps/${eventId}/${sponsorship.sponsor_registration_id}`,
                        );
                      }}>
                      {
                        ApplicationString[
                          hasUnpaidAddedVendorReps
                            ? 'sponsorshipReps.editReps'
                            : 'sponsorshipReps.addReps'
                        ]
                      }
                    </Button>
                  )}
                </div>
              }>
              {isSponsorshipActive && !sponsorship?.can_edit_reps && (
                <Alert
                  className='sponsorship-reps-alert'
                  type='info'
                  showIcon
                  message={ApplicationString['sponsorshipReps.changesClosed']}
                />
              )}
              {isSponsorshipActive &&
                sponsorship?.can_edit_reps &&
                limitMessage && (
                  <Alert
                    className='sponsorship-reps-alert'
                    type='info'
                    showIcon
                    message={limitMessage}
                  />
                )}

              <Tabs
                prefixCls='user-event-card'
                // Latest payment first, like the per-payment tabs before.
                items={(sponsorship?.transactions || [])
                  .map((transaction, index) => {
                    const paidAmount =
                      transaction?.amount !== null &&
                      transaction?.amount !== undefined
                        ? transaction.amount
                        : transaction?.total_cost;
                    const paymentDate =
                      transaction?.payment_date ||
                      transaction?.registrations?.[0]?.registration_date;
                    const transactionActions = renderTransactionActions?.({
                      key: transaction?.transaction_id,
                      attendees: transaction?.registrations,
                      optionId:
                        sponsor?.event_option_id ||
                        sponsorshipOption?.event_option_id,
                      // Same 7-day window as Add Vendor Show Attendance / API can_edit_reps.
                      can_edit_reps: sponsorship?.can_edit_reps,
                    });
                    return {
                      key: transaction?.transaction_id || `pending-${index}`,
                      label: `${
                        ApplicationString['userEventDetailsModal.payment']
                      }${index + 1}`,
                      children: (
                        <div className='sponsorship-transaction'>
                          <div className='table-edit-container'>
                            <div className='table-cost-amount'>
                              <p className='table-total-cost'>
                                <strong>
                                  {ApplicationString['sponsorshipReps.payment']}{' '}
                                  {index + 1}:{' '}
                                  {transaction?.is_original_purchase
                                    ? ApplicationString[
                                        'sponsorshipReps.originalPurchase'
                                      ]
                                    : ApplicationString[
                                        'sponsorshipReps.addedReps'
                                      ]}
                                </strong>
                                <span style={{marginLeft: '15px'}}>
                                  {
                                    ApplicationString[
                                      'userEventDetailsModal.totalCost'
                                    ]
                                  }
                                  :{' '}
                                  {formatCurrencyByLocalChapter(
                                    paidAmount || 0,
                                    eventLocalChapterData,
                                    2,
                                    2,
                                  )}
                                </span>
                                {paymentDate && (
                                  <span style={{marginLeft: '15px'}}>
                                    {
                                      ApplicationString[
                                        'userEventDetailsModal.paymentDate'
                                      ]
                                    }
                                    :{' '}
                                    {formatUtcToLocalDateTime(paymentDate) ||
                                      'N/A'}
                                  </span>
                                )}
                              </p>
                            </div>
                            {transactionActions}
                          </div>
                          <Table
                            prefixCls='event-details-table'
                            rowKey='id'
                            dataSource={transaction?.registrations || []}
                            scroll={{x: 1000}}
                            columns={tableColumns}
                            size='small'
                            pagination={false}
                          />
                        </div>
                      ),
                    };
                  })
                  .reverse()}
              />
            </Collapse.Panel>
          );
        })}
      </Collapse>
    </>
  );
};

export default SponsorshipGroups;
